import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CustomHttpErrorResponse,
  DatepickerRangeModel,
  Error,
  validateAndProcessTimeFrame,
} from '../../../../../common';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { WorkingHoursCalendarModel } from '../../../administration';
import { AllocationViewFilterName } from '../constants';
import {
  AllocationCalendarDock,
  AllocationCalendarDockModel,
  AllocationCalendarSlot,
} from '../models/allocation-view-calendar';
import {
  AllocationViewCalendarInitialState,
  AllocationViewCalendarLoad,
  AllocationViewCalendarSelectSlot,
  AllocationViewCalendarUpdateDocksAfterDrop,
} from './allocation-view-calendar.actions';
import { AllocationViewCalendarService } from './allocation-view-calendar.service';
import { isAfter, isBefore } from 'date-fns';
import { isEqual } from 'lodash';

export interface AllocationViewCalendarStateModel {
  selectedSlot: AllocationCalendarSlot;
  data: AllocationCalendarDockModel[];
  loaded: boolean;
}

const initialState: AllocationViewCalendarStateModel = {
  selectedSlot: null,
  data: null,
  loaded: false,
};

@State<AllocationViewCalendarStateModel>({
  name: 'allocationViewCalendar',
  defaults: initialState,
})
@Injectable()
export class AllocationViewCalendarState {
  constructor(
    private allocationViewCalendarService: AllocationViewCalendarService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getCalendarData(state: AllocationViewCalendarStateModel): AllocationCalendarDockModel[] {
    return state.data;
  }

  @Selector()
  static getSelectedSlot(state: AllocationViewCalendarStateModel): AllocationCalendarSlot {
    return state.selectedSlot;
  }

  @Selector()
  static getLoaded(state: AllocationViewCalendarStateModel): boolean {
    return state.loaded;
  }

  @Action(AllocationViewCalendarLoad)
  getAllocationViewCalendar(
    ctx: StateContext<AllocationViewCalendarStateModel>,
    { payload }: AllocationViewCalendarLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    payload[AllocationViewFilterName.TimeFrame] = validateAndProcessTimeFrame(
      payload[AllocationViewFilterName.TimeFrame],
    );
    return this.allocationViewCalendarService.getAllocationViewCalendar(payload).pipe(
      map(this.slotsMapper(payload[AllocationViewFilterName.TimeFrame])),
      map((data: AllocationCalendarDock[]) => {
        ctx.patchState({
          data,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(AllocationViewCalendarSelectSlot)
  selectSlot(ctx: StateContext<AllocationViewCalendarStateModel>, { payload }: AllocationViewCalendarSelectSlot): void {
    ctx.patchState({
      selectedSlot: payload,
    });
  }

  @Action(AllocationViewCalendarUpdateDocksAfterDrop)
  updateDocksAfterDrop(
    ctx: StateContext<AllocationViewCalendarStateModel>,
    { payload }: AllocationViewCalendarUpdateDocksAfterDrop,
  ): void {
    const { data } = ctx.getState();
    // TODO: try to simplify it
    const droppedSlot: AllocationCalendarSlot = data
      .find((dock: AllocationCalendarDock) => dock.dockId === payload.originalDockId)
      .slots.find((slot: AllocationCalendarSlot) => slot.slotId === payload.slotId);

    const docks: AllocationCalendarDockModel[] = data.map((dock: AllocationCalendarDockModel) => {
      if (payload.originalDockId !== payload.allocationDockId) {
        if (dock.dockId === payload.originalDockId) {
          return {
            ...dock,
            slots: dock.slots.filter((slot: AllocationCalendarSlot) => slot.slotId !== payload.slotId),
          };
        }

        if (dock.dockId === payload.allocationDockId) {
          return { ...dock, slots: [...dock.slots, { ...droppedSlot, timeFrame: payload.timeRange }] };
        }
      } else {
        const slotIndex: number = dock.slots.findIndex(
          (slot: AllocationCalendarSlot) => slot.slotId === payload.slotId,
        );

        if (slotIndex >= 0) {
          const modifiedSlots: AllocationCalendarSlot[] = [...dock.slots];
          modifiedSlots.splice(slotIndex, 1, { ...droppedSlot, timeFrame: payload.timeRange });

          return { ...dock, slots: modifiedSlots };
        }
      }

      return dock;
    });

    ctx.patchState({
      data: docks,
    });
  }

  @Action(AllocationViewCalendarInitialState)
  setInitialState(ctx: StateContext<AllocationViewCalendarInitialState>): void {
    ctx.setState(initialState);
  }

  private slotsMapper(
    timeFrame: DatepickerRangeModel,
  ): (slots: AllocationCalendarDock[]) => Partial<AllocationCalendarDock>[] {
    return (slots: AllocationCalendarDock[]) =>
      slots.map((slot: AllocationCalendarDock) => {
        return {
          ...slot,
          workingHoursView: {
            ...slot.workingHoursView,
            workingHours: slot.workingHoursView.workingHours.filter((workingHour: WorkingHoursCalendarModel) =>
              this.isSlotInsideTimeRange(workingHour, timeFrame),
            ),
          },
        };
      });
  }

  private isSlotInsideTimeRange(workingHour: WorkingHoursCalendarModel, timeFrame: DatepickerRangeModel): boolean {
    return (
      isAfter(new Date(workingHour.start.timestamp), timeFrame.start) ||
      isEqual(new Date(workingHour.start.timestamp), timeFrame.start) ||
      isBefore(new Date(workingHour.start.timestamp), timeFrame.end) ||
      isEqual(new Date(workingHour.start.timestamp), timeFrame.end)
    );
  }
}
